import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnline } from '../../hooks/useOnline';

interface Props {
  /** Full screen was on for this section and the browser left it. */
  fullscreenLost: boolean;
  onReturnToFullscreen: () => void;
  /** Sending answers is failing; they are kept and retried. */
  saveFailed?: boolean;
  onRetrySave?: () => void;
  /** Title of the section the clock just closed. */
  timeUpTitle?: string | null;
  onDismissTimeUp?: () => void;
  /** Title of the section the candidate came back to after a reload. */
  resumedTitle?: string | null;
  onDismissResumed?: () => void;
}

const BACK_ONLINE_MS = 4000;
const RESUMED_MS = 8000;

const BASE = 'shrink-0 px-4 py-1.5 text-center text-xs font-semibold leading-snug sm:px-6 sm:py-2 sm:text-[13px]';
const ROW = `${BASE} flex flex-wrap items-center justify-center gap-x-3 gap-y-1`;
const ACTION =
  'rounded-lg px-3 py-1.5 text-[13px] font-bold max-sm:min-h-11 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B]';

/** The exam's passing notices: connection, saving, full screen, time up, and coming back after a reload. The clock keeps running through all of them. */
export function SessionBanners({
  fullscreenLost,
  onReturnToFullscreen,
  saveFailed = false,
  onRetrySave,
  timeUpTitle = null,
  onDismissTimeUp,
  resumedTitle = null,
  onDismissResumed,
}: Props) {
  const { t } = useTranslation();
  const online = useOnline();
  const wasOffline = useRef(false);
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    if (!online) {
      wasOffline.current = true;
      setRestored(false);
      return undefined;
    }
    if (!wasOffline.current) return undefined;
    wasOffline.current = false;
    setRestored(true);
    const id = setTimeout(() => setRestored(false), BACK_ONLINE_MS);
    return () => clearTimeout(id);
  }, [online]);

  useEffect(() => {
    if (!resumedTitle || !onDismissResumed) return undefined;
    const id = setTimeout(onDismissResumed, RESUMED_MS);
    return () => clearTimeout(id);
  }, [resumedTitle, onDismissResumed]);

  return (
    <>
      {!online && (
        <p role="alert" className={`${BASE} bg-[#FDECEC] text-[#7A1A12]`}>
          {t('exam.banner.offline')}
        </p>
      )}
      {online && restored && (
        <p role="status" className={`${BASE} bg-[#E6F4EC] text-[#14532D]`}>
          {t('exam.banner.online_again')}
        </p>
      )}
      {online && saveFailed && (
        <div role="alert" className={`${ROW} bg-[#FDECEC] text-[#7A1A12]`}>
          {t('exam.banner.save_failed')}
          {onRetrySave && (
            <button type="button" onClick={onRetrySave} className={`${ACTION} bg-white text-[#10182B]`}>
              {t('exam.banner.retry_now')}
            </button>
          )}
        </div>
      )}
      {timeUpTitle !== null && (
        <div role="status" className={`${ROW} bg-[#E8EEFF] text-[#1E3A8A]`}>
          {t('exam.banner.time_up', { section: timeUpTitle })}
          {onDismissTimeUp && (
            <button type="button" onClick={onDismissTimeUp} className={`${ACTION} bg-white text-[#10182B]`}>
              {t('exam.nav.close')}
            </button>
          )}
        </div>
      )}
      {resumedTitle && (
        <p role="status" className={`${BASE} bg-[#E8EEFF] text-[#1E3A8A]`}>
          {t('exam.banner.resumed', { section: resumedTitle })}
        </p>
      )}
      {fullscreenLost && (
        <div role="status" className={`${ROW} bg-[#FFF4D6] text-[#92400E]`}>
          {t('exam.banner.fullscreen_left')}
          <button type="button" onClick={onReturnToFullscreen} className={`${ACTION} bg-[#D1FE17] text-[#10182B]`}>
            {t('exam.banner.fullscreen_return')}
          </button>
        </div>
      )}
    </>
  );
}

export default SessionBanners;
